import { useAuth } from '@eg/compartido'
import { LogOut } from 'lucide-react'
import { lazy, Suspense, type ReactNode } from 'react'
import { Link, NavLink, Route, Routes } from 'react-router-dom'
import { es } from '../content/es'
import { Puerta, useMfaRequerida } from '../features/acceso/Puerta'
import { GuiaVisual } from './GuiaVisual'

const Tablero = lazy(() => import('../features/tablero/Tablero').then((m) => ({ default: m.Tablero })))
const ListaEmpresas = lazy(() => import('../features/empresas/ListaEmpresas').then((m) => ({ default: m.ListaEmpresas })))
const ListaProductos = lazy(() => import('../features/productos/ListaProductos').then((m) => ({ default: m.ListaProductos })))
const FormProducto = lazy(() => import('../features/productos/FormProducto').then((m) => ({ default: m.FormProducto })))
const ListaPedidos = lazy(() => import('../features/pedidos/ListaPedidos').then((m) => ({ default: m.ListaPedidos })))
const DetallePedido = lazy(() => import('../features/pedidos/DetallePedido').then((m) => ({ default: m.DetallePedido })))
const Configuracion = lazy(() => import('../features/configuracion/Configuracion').then((m) => ({ default: m.Configuracion })))
const DetalleEmpresa = lazy(() => import('../features/empresas/DetalleEmpresa').then((m) => ({ default: m.DetalleEmpresa })))

function Layout({ children }: { children: ReactNode }) {
  const { sesion, nivelActual, recuperando, cerrarSesion } = useAuth()
  const mfa = useMfaRequerida()
  const dentro = sesion !== null && !recuperando && mfa.isSuccess && (!mfa.data || nivelActual === 'aal2')
  return (
    <div className="pagina">
      <a className="saltar-al-contenido" href="#contenido">
        {es.saltar}
      </a>
      <header className="encabezado sobre-marino">
        <div className="contenedor encabezado__fila">
          <Link className="marca" to="/" aria-label={`${es.marca} ${es.zona}, ir al inicio`}>
            {es.marca}
            <span className="marca__zona">{es.zona}</span>
          </Link>
          {dentro && (
            <div className="encabezado__acciones">
              <nav className="navegacion" aria-label={es.navegacionPrincipal}>
                <ul>
                  <li>
                    <NavLink to="/" end>
                      {es.nav.tablero}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/empresas">{es.nav.empresas}</NavLink>
                  </li>
                  <li>
                    <NavLink to="/productos">{es.nav.productos}</NavLink>
                  </li>
                  <li>
                    <NavLink to="/pedidos">{es.nav.pedidos}</NavLink>
                  </li>
                  <li>
                    <NavLink to="/configuracion">{es.nav.configuracion}</NavLink>
                  </li>
                </ul>
              </nav>
              <button type="button" className="boton boton--sobre-marino boton--compacto" onClick={() => void cerrarSesion()}>
                <LogOut className="icono" strokeWidth={1.75} aria-hidden="true" />
                {es.cerrarSesion}
              </button>
            </div>
          )}
        </div>
      </header>
      <main id="contenido" className="principal contenedor" tabIndex={-1}>
        {children}
      </main>
      <footer className="pie">
        <div className="contenedor">{es.pie}</div>
      </footer>
    </div>
  )
}

function NoEncontrada() {
  return (
    <>
      <h1>{es.noEncontrada.titulo}</h1>
      <p className="principal__intro">{es.noEncontrada.texto}</p>
      <p className="accion">
        <Link className="boton boton--primario" to="/">
          {es.noEncontrada.volver}
        </Link>
      </p>
    </>
  )
}

export function App() {
  return (
    <Layout>
      <Routes>
        {import.meta.env.DEV && <Route path="/guia" element={<GuiaVisual />} />}
        {import.meta.env.DEV && <Route path="/guia/producto" element={<FormProducto />} />}
        <Route
          path="*"
          element={
            <Puerta>
              <Suspense
                fallback={
                  <div role="status" aria-live="polite">
                    <div className="esqueleto" />
                  </div>
                }
              >
              <Routes>
                <Route path="/" element={<Tablero />} />
                <Route path="/empresas" element={<ListaEmpresas />} />
                <Route path="/empresas/:id" element={<DetalleEmpresa />} />
                <Route path="/pedidos" element={<ListaPedidos />} />
                <Route path="/pedidos/:id" element={<DetallePedido />} />
                <Route path="/configuracion" element={<Configuracion />} />
                <Route path="/productos" element={<ListaProductos />} />
                <Route path="/productos/nuevo" element={<FormProducto />} />
                <Route path="/productos/:id" element={<FormProducto />} />
                <Route path="*" element={<NoEncontrada />} />
              </Routes>
              </Suspense>
            </Puerta>
          }
        />
      </Routes>
    </Layout>
  )
}
